import React from 'react';
import styled from 'styled-components';
import Availability from '../components/Availability';
import { FaCalendarAlt, FaInfoCircle, FaCreditCard, FaCheckCircle, FaStar } from 'react-icons/fa';
import { seasons, specialPeriods } from '../config/priceConfig';
import Icon from '../components/Icon';

const AvailabilityPage: React.FC = () => {
  return (
    <AvailabilityPageContainer>
      <div className="container">
        <AvailabilityHeader>
          <AvailabilityTitle>Disponibilités & Réservation</AvailabilityTitle>
          <AvailabilityDescription>
            Vérifiez les disponibilités de notre villa et réservez votre séjour aux dates qui vous conviennent. Nous proposons des tarifs flexibles selon la saison et la durée de votre séjour.
          </AvailabilityDescription>
        </AvailabilityHeader>
        
        <AvailabilityContent>
          <AvailabilityCalendar>
            <Availability />
          </AvailabilityCalendar>
          
          <AvailabilityInfo>
            <InfoSection>
              <InfoTitle>
                <Icon component={FaInfoCircle} />
                <span>Informations de réservation</span>
              </InfoTitle>
              <InfoList>
                <InfoItem>Arrivée à partir de 16h00</InfoItem>
                <InfoItem>Départ avant 10h00</InfoItem>
                <InfoItem>Séjour minimum : 5 nuits</InfoItem>
                <InfoItem>Capacité maximale : 8 personnes</InfoItem>
                <InfoItem>Animaux acceptés</InfoItem>

              </InfoList>
            </InfoSection>
            
            <InfoSection>
              <InfoTitle>
                <Icon component={FaCreditCard} />
                <span>Conditions d'annulation fermes</span>
              </InfoTitle>
              <InfoList>
                <InfoItem>Annulation possible sans frais jusqu'à 30 jours avant l'arrivée</InfoItem>
              </InfoList>
            </InfoSection>
            
            <InfoSection>
              <InfoTitle>
                <Icon component={FaCalendarAlt} />
                <span>Tarifs saisonniers</span>
              </InfoTitle>
              <PriceTable>
                <thead>
                  <tr>
                    <th>Saison</th>
                    <th>Période</th>
                    <th>Prix / nuit</th>
                  </tr>
                </thead>
                <tbody>
                  {seasons.map((season, index) => (
                    <tr key={`season-${index}`}>
                      <td>{season.name}</td>
                      <td>{season.description}</td>
                      <td>{season.pricePerNight}€</td>
                    </tr>
                  ))}
                </tbody>
              </PriceTable>
            </InfoSection>
            
            {specialPeriods.length > 0 && (
              <InfoSection>
                <InfoTitle>
                  <Icon component={FaStar} />
                  <span>Périodes spéciales</span>
                </InfoTitle>
                <PriceTable>
                  <thead>
                    <tr>
                      <th>Événement</th>
                      <th>Dates</th>
                      <th>Prix / nuit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specialPeriods.map((period, index) => (
                      <tr key={`special-${index}`}>
                        <td>{period.name}</td>
                        <td>
                          {period.startDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} - 
                          {period.endDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </td>
                        <td>{period.pricePerNight}€</td>
                      </tr>
                    ))}
                  </tbody>
                </PriceTable>
              </InfoSection>
            )}
            
            <InfoSection>
              <InfoTitle>
                <Icon component={FaCheckCircle} />
                <span>Services inclus</span>
              </InfoTitle>
              <ServicesList>
                <ServicesItem>Ménage de fin de séjour (200 €)</ServicesItem>
                <ServicesItem>Linge de lit et serviettes</ServicesItem>
                <ServicesItem>WiFi haut débit</ServicesItem>
                <ServicesItem>Produits d'accueil</ServicesItem>
                <ServicesItem>Parking privé</ServicesItem>
              </ServicesList>
            </InfoSection>
          </AvailabilityInfo>
        </AvailabilityContent>
      </div>
    </AvailabilityPageContainer>
  );
};

const AvailabilityPageContainer = styled.div`
  padding: 12rem 0 8rem;
`;

const AvailabilityHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;
`;

const AvailabilityTitle = styled.h1`
  margin-bottom: 2rem;
`;

const AvailabilityDescription = styled.p`
  max-width: 80rem;
  margin: 0 auto 3rem;
  font-size: 1.8rem;
  color: var(--color-secondary);
`;

const AvailabilityContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4rem;
  
  @media (min-width: 992px) {
    grid-template-columns: 3fr 2fr;
  }
`;

const AvailabilityCalendar = styled.div``;

const AvailabilityInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const InfoSection = styled.div`
  background-color: var(--color-light);
  padding: 2.5rem;
  box-shadow: var(--shadow);
`;

const InfoTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 2rem;
  margin-bottom: 2rem;
  color: var(--color-primary);
  
  svg {
    color: var(--color-accent);
  }
`;

const InfoList = styled.ul`
  list-style: none;
`;

const InfoItem = styled.li`
  position: relative;
  padding-left: 2rem;
  margin-bottom: 1rem;
  font-size: 1.6rem;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.8rem;
    width: 0.8rem;
    height: 0.8rem;
    background-color: var(--color-accent);
  }
`;

const PriceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.5rem;
  
  th, td {
    padding: 1.2rem 1rem;
    text-align: left;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
  
  th {
    font-weight: 600;
    color: var(--color-primary);
  }
  
  tr:last-child td {
    border-bottom: none;
  }
`;

const ServicesList = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServicesItem = styled.li`
  position: relative;
  padding-left: 2rem;
  font-size: 1.5rem;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--color-accent);
    font-weight: bold;
  }
`;

export default AvailabilityPage;
